import React from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";

export interface RelatedLink {
  title: string;
  href: string;
  description?: string;
}

/**
 * Reusable "Related Pages" / cross-linking section.
 * Renders a clean, accessible grid of internal links for SEO backlinking.
 * Drop it near the end of any page (before Footer or Consultation).
 */
export const RelatedPages: React.FC<{
  heading?: string;
  links: RelatedLink[];
  className?: string;
}> = ({ heading = "Related Pages", links, className = "" }) => {
  if (!links || links.length === 0) return null;

  return (
    <section className={`bg-background py-10 sm:py-12 ${className}`}>
      <div className="container-px mx-auto max-w-5xl">
        <h2 className="font-headings text-lg sm:text-xl font-bold text-primary mb-4">
          {heading}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {links.map((link, idx) => (
            <Link
              key={idx}
              to={link.href}
              className="group rounded-lg border border-border bg-card p-4 shadow-soft hover:border-accent/40 hover:shadow-premium transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5">
                  <h3 className="font-headings text-sm font-bold text-primary group-hover:text-accent transition-colors leading-snug">
                    {link.title}
                  </h3>
                  {link.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {link.description}
                    </p>
                  )}
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Compact inline link list — for DecisionPageLayout pages that use a narrow
 * single-column content area.
 */
export const InlineRelatedLinks: React.FC<{
  heading?: string;
  links: RelatedLink[];
}> = ({ heading = "Explore Related Pages", links }) => {
  if (!links || links.length === 0) return null;

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-7 shadow-soft space-y-4">
      <h2 className="font-headings text-lg sm:text-xl font-bold text-primary">
        {heading}
      </h2>
      <div className="flex flex-col gap-2">
        {links.map((link, idx) => (
          <Link
            key={idx}
            to={link.href}
            className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline underline-offset-2"
          >
            <ArrowRight className="h-4 w-4 shrink-0" />
            {link.title}
          </Link>
        ))}
      </div>
    </div>
  );
};

/* ── Pre-defined link sets for common page groups ── */

export const allTreatmentsLinks: RelatedLink[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "FDA-cleared, 5-day accelerated TMS protocol",
  },
  {
    title: "Standard TMS Therapy",
    href: "/services/tms",
    description: "Non-invasive magnetic stimulation for depression",
  },
  {
    title: "SPRAVATO® (Esketamine)",
    href: "/services/spravato",
    description: "FDA-approved nasal spray for TRD",
  },
  {
    title: "Prism for PTSD",
    href: "/prism",
    description: "EEG neurofeedback for PTSD symptoms",
  },
  {
    title: "Proliv®Rx At-Home TMS",
    href: "/prolivrx",
    description: "At-home neuromodulation device",
  },
  {
    title: "Ampa One System",
    href: "/ampa",
    description: "Single-day accelerated TMS protocol",
  },
  {
    title: "IV Ketamine Infusions",
    href: "/ketamine-treatment-coral-springs-fl",
    description: "Rapid-acting IV ketamine therapy",
  },
  {
    title: "Ketamine-Assisted Psychotherapy",
    href: "/ketamine-assisted-psychotherapy-coral-springs-fl",
    description: "KAP combines ketamine with therapy",
  },
  {
    title: "PrTMS Therapy",
    href: "/prtms-therapy-coral-springs-fl",
    description: "Personalized repetitive TMS",
  },
  {
    title: "Psychotherapy & Counseling",
    href: "/services/psychotherapy",
    description: "CBT, DBT, EMDR, and more",
  },
  {
    title: "Medication Management",
    href: "/services/medication-management",
    description: "Psychiatric medication oversight",
  },
  {
    title: "Ampa One Day Accelerated Protocol",
    href: "/ampa",
    description: "Single-day accelerated protocol",
  },
];

export const allServiceLinks: RelatedLink[] = [
  {
    title: "TMS for Depression",
    href: "/services/depression",
    description: "FDA-cleared TMS for treatment-resistant depression",
  },
  {
    title: "TMS for Anxiety",
    href: "/services/anxiety",
    description: "Non-drug neuromodulation for anxiety",
  },
  {
    title: "TMS for PTSD",
    href: "/services/ptsd",
    description: "Targeted stimulation for trauma circuits",
  },
  {
    title: "TMS for OCD",
    href: "/services/ocd",
    description: "SmartFocus® 3D targeting for OCD",
  },
];

/**
 * TMS-by-condition cluster — used on every TMS page so each links out to
 * the other TMS pages with descriptive anchor text (internal linking for
 * topical authority on "TMS therapy" + per-condition variants).
 */
export const allTmsByConditionLinks: RelatedLink[] = [
  {
    title: "What Is TMS Therapy?",
    href: "/contents/tms/what-is-tms-therapy",
    description: "How transcranial magnetic stimulation works",
  },
  {
    title: "TMS Therapy in Coral Springs, FL",
    href: "/tms-coral-springs-fl",
    description: "Our Coral Springs treatment center and service area",
  },
  {
    title: "TMS for Depression",
    href: "/services/depression",
    description: "FDA-cleared TMS for treatment-resistant depression",
  },
  {
    title: "TMS for Anxiety",
    href: "/services/anxiety",
    description: "Non-drug neuromodulation for anxiety",
  },
  {
    title: "TMS for OCD",
    href: "/services/ocd",
    description: "SmartFocus® 3D targeting for OCD",
  },
  {
    title: "TMS for PTSD",
    href: "/services/ptsd",
    description: "Targeted stimulation for trauma circuits",
  },
];

export const allConditionLinks: RelatedLink[] = [
  {
    title: "Ketamine for Anxiety",
    href: "/anxiety-treatment-coral-springs-fl",
  },
  {
    title: "Ketamine for Depression",
    href: "/depression-treatment-coral-springs-fl",
  },
  { title: "Ketamine for PTSD", href: "/ptsd-treatment-coral-springs-fl" },
  { title: "Ketamine for OCD", href: "/ocd-treatment-coral-springs-fl" },
  {
    title: "Ketamine for Bipolar Disorder",
    href: "/bipolar-disorder-coral-springs-fl",
  },
  {
    title: "Ketamine for Chronic Pain",
    href: "/chronic-pain-coral-springs-fl",
  },
  { title: "Ketamine for CRPS", href: "/crps-treatment-coral-springs-fl" },
  {
    title: "Ketamine for Fibromyalgia",
    href: "/fibromyalgia-treatment-coral-springs-fl",
  },
  {
    title: "Ketamine for Migraines",
    href: "/migraines-treatment-coral-springs-fl",
  },
  {
    title: "Ketamine for Neuropathic Pain",
    href: "/neuropathic-pain-treatment-coral-springs-fl",
  },
  {
    title: "Ketamine for Suicidal Ideation",
    href: "/suicidal-ideation-treatment-coral-springs-fl",
  },
];

export const allDecisionLinks: RelatedLink[] = [
  {
    title: "Traveling for Treatment",
    href: "/traveling-for-treatment",
    description: "What the week looks like, hotels, what to bring",
  },
  {
    title: "Verify a SAINT Provider",
    href: "/find-a-verified-saint-provider",
    description: "How to confirm a center truly offers SAINT",
  },
  {
    title: "SAINT vs. Standard TMS",
    href: "/learn/saint-vs-traditional-tms",
    description: "Key differences in targeting, schedule, and duration",
  },
  {
    title: "I've Already Tried TMS",
    href: "/already-tried-tms",
    description: "Prior TMS doesn't disqualify you",
  },
  {
    title: "How Insurance Works",
    href: "/how-insurance-works",
    description: "Benefits verification, authorization, appeals",
  },
  {
    title: "Am I a Candidate?",
    href: "/am-i-a-candidate",
    description: "Screening factors — not disqualifiers",
  },
  {
    title: "Compare Treatments",
    href: "/compare-treatments",
    description: "Side-by-side matrix of all treatments",
  },
  {
    title: "For Families & Caregivers",
    href: "/for-families",
    description: "Supporting someone through treatment",
  },
  {
    title: "Cost & Insurance",
    href: "/cost-insurance",
    description: "Financing options and insurance information",
  },
];

export const allSpravatoScenarioLinks: RelatedLink[] = [
  {
    title: "SPRAVATO® Overview",
    href: "/services/spravato",
    description: "How SPRAVATO® works for treatment-resistant depression",
  },
  {
    title: "What to Expect at Your First Session",
    href: "/spravato-what-to-expect",
    description: "A step-by-step walkthrough",
  },
  {
    title: '"I\'ve Tried Everything"',
    href: "/spravato-tried-everything",
    description: "When the list of antidepressants gets long",
  },
  {
    title: "Functioning, and Not Okay",
    href: "/spravato-high-functioning-depression",
    description: "High-functioning depression",
  },
  {
    title: "When You're the Caregiver",
    href: "/spravato-caregivers",
    description: "Your own appointment keeps getting moved",
  },
  {
    title: "After a Loss",
    href: "/spravato-grief-depression",
    description: "When grief becomes something else",
  },
  {
    title: "Tired of Medication",
    href: "/spravato-tired-of-medication",
    description: "A reasonable position, honestly",
  },
  {
    title: "Retired, and It Got Worse",
    href: "/spravato-older-adults",
    description: "Depression later in life",
  },
  {
    title: "When You Can't Take a Week Off",
    href: "/spravato-fits-your-schedule",
    description: "Treatment that fits your schedule",
  },
  {
    title: "When Insurance Said No",
    href: "/spravato-insurance-denied",
    description: "Prior authorization and appeals",
  },
];

export const allEducationLinks: RelatedLink[] = [
  {
    title: "What is TMS Therapy?",
    href: "/contents/tms/what-is-tms-therapy",
    description: "Understand how TMS works",
  },
  {
    title: "Is TMS Right for Me?",
    href: "/contents/tms/is-tms-right-for-me",
    description: "Candidacy and screening",
  },
  {
    title: "Typical TMS Treatment",
    href: "/contents/tms/typical-tms-treatment",
    description: "What a course looks like",
  },
  {
    title: "TMS Clinical Study Results",
    href: "/contents/tms/tms-clinical-study-results",
    description: "Research findings",
  },
  {
    title: "About Depression",
    href: "/contents/conditions/depression/about-depression",
    description: "Understanding the condition",
  },
  {
    title: "Depression Symptoms",
    href: "/contents/conditions/depression/depression-symptoms",
    description: "Recognizing the signs",
  },
  {
    title: "Treating Depression",
    href: "/contents/conditions/depression/treating-depression",
    description: "Treatment approaches",
  },
  {
    title: "Videos & Media",
    href: "/contents/patient-info/videos-media",
    description: "Watch patient stories",
  },
  {
    title: "Financing Options",
    href: "/contents/patient-info/financing-options",
    description: "Payment and financing",
  },
  {
    title: "Supplemental Research",
    href: "/supplemental-research",
    description: "Additional studies and data",
  },
  {
    title: "Virtual Education",
    href: "/virtual-education",
    description: "Online learning resources",
  },
];

export const allTeamLinks: RelatedLink[] = [
  {
    title: "Dr. Daniel Pietras, MD",
    href: "/dr-daniel-pietras-md",
    description: "Medical Director & Board-Certified Psychiatrist",
  },
  {
    title: "Nyoka Powell Robinson, PMHNP-BC",
    href: "/nyoka-powell-robinson",
    description: "Psychiatric Nurse Practitioner",
  },
  {
    title: "Olivia Sierra, LCSW",
    href: "/olivia-sierra",
    description: "Primary Therapist",
  },
  {
    title: "Timitra Martin, RCSW",
    href: "/timitra-martin",
    description: "Clinical Social Worker",
  },
  {
    title: "Roxana Carmenate, LCSW",
    href: "/roxana-carmenate",
    description: "Ketamine-Assisted EMDR Specialist",
  },
];

export const allAcceleratedTmsGuideLinks: RelatedLink[] = [
  {
    title: "Verify a SAINT Provider",
    href: "/find-a-verified-saint-provider",
    description:
      "How to tell whether a clinic is actually credentialed for SAINT",
  },
  {
    title: "The fMRI Step",
    href: "/accelerated-tms-fmri-targeting",
    description: "Why fMRI targeting separates SAINT from conventional TMS",
  },
  {
    title: "What Coverage Looks Like",
    href: "/accelerated-tms-coverage",
    description: "Insurance, Medicare, and Category III billing codes",
  },
  {
    title: "The Five-Day Week",
    href: "/accelerated-tms-five-day-week",
    description: "What the treatment week actually involves",
  },
  {
    title: "Other Conditions",
    href: "/accelerated-tms-other-conditions",
    description: "Where accelerated protocols stand for OCD, anxiety, PTSD",
  },
];

export const allPrTmsLinks: RelatedLink[] = [
  {
    title: "PrTMS Therapy Overview",
    href: "/prtms-therapy-coral-springs-fl",
    description: "Personalized repetitive TMS",
  },
  {
    title: "PrTMS for ADHD",
    href: "/prtms-for-adhd-coral-springs-fl",
    description: "PrTMS for attention disorders",
  },
  {
    title: "PrTMS for Autism",
    href: "/prtms-for-autism-coral-springs-fl",
    description: "PrTMS for autism spectrum",
  },
  {
    title: "LP-TMS Therapy",
    href: "/lp-tms-therapy-coral-springs-florida",
    description: "Large-platform TMS",
  },
  {
    title: "Ampa One Day Accelerated Protocol",
    href: "/ampa",
    description: "Single-day protocol",
  },
];
